import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import {
  Bold as BoldIcon, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Link2, Image as ImageIcon, AlignLeft, AlignCenter, AlignRight, Heading1, Heading2, Quote, Undo2, Redo2,
} from "lucide-react";

export default function RichTextEditor({ value, onChange, placeholder = "Write your message…", editable = true }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
      Placeholder.configure({ placeholder }),
      Image.configure({ inline: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "",
    editable,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line
  }, [value]);

  if (!editor) return null;

  const btn = "h-8 w-8 grid place-items-center rounded-md text-muted-foreground hover:bg-muted transition-colors";
  const on = "text-emerald-brand bg-emerald-brand/12";

  const addLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
  };

  const addImage = () => {
    const url = window.prompt("Image URL");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  return (
    <div className="prose-editor border border-border rounded-xl overflow-hidden bg-card" data-testid="rich-editor">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border p-1.5 bg-muted/40">
        <button type="button" className={`${btn} ${editor.isActive("bold") ? on : ""}`} onClick={() => editor.chain().focus().toggleBold().run()} data-testid="rt-bold" aria-label="Bold"><BoldIcon className="h-3.5 w-3.5" /></button>
        <button type="button" className={`${btn} ${editor.isActive("italic") ? on : ""}`} onClick={() => editor.chain().focus().toggleItalic().run()} data-testid="rt-italic" aria-label="Italic"><Italic className="h-3.5 w-3.5" /></button>
        <button type="button" className={`${btn} ${editor.isActive("underline") ? on : ""}`} onClick={() => editor.chain().focus().toggleUnderline().run()} data-testid="rt-underline" aria-label="Underline"><UnderlineIcon className="h-3.5 w-3.5" /></button>
        <div className="w-px h-5 bg-border mx-1" />
        <button type="button" className={`${btn} ${editor.isActive("heading", { level: 1 }) ? on : ""}`} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} data-testid="rt-h1" aria-label="H1"><Heading1 className="h-3.5 w-3.5" /></button>
        <button type="button" className={`${btn} ${editor.isActive("heading", { level: 2 }) ? on : ""}`} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} data-testid="rt-h2" aria-label="H2"><Heading2 className="h-3.5 w-3.5" /></button>
        <div className="w-px h-5 bg-border mx-1" />
        <button type="button" className={`${btn} ${editor.isActive("bulletList") ? on : ""}`} onClick={() => editor.chain().focus().toggleBulletList().run()} data-testid="rt-ul" aria-label="Bulleted list"><List className="h-3.5 w-3.5" /></button>
        <button type="button" className={`${btn} ${editor.isActive("orderedList") ? on : ""}`} onClick={() => editor.chain().focus().toggleOrderedList().run()} data-testid="rt-ol" aria-label="Numbered list"><ListOrdered className="h-3.5 w-3.5" /></button>
        <button type="button" className={`${btn} ${editor.isActive("blockquote") ? on : ""}`} onClick={() => editor.chain().focus().toggleBlockquote().run()} data-testid="rt-quote" aria-label="Quote"><Quote className="h-3.5 w-3.5" /></button>
        <div className="w-px h-5 bg-border mx-1" />
        <button type="button" className={`${btn} ${editor.isActive({ textAlign: "left" }) ? on : ""}`} onClick={() => editor.chain().focus().setTextAlign("left").run()} aria-label="Left"><AlignLeft className="h-3.5 w-3.5" /></button>
        <button type="button" className={`${btn} ${editor.isActive({ textAlign: "center" }) ? on : ""}`} onClick={() => editor.chain().focus().setTextAlign("center").run()} aria-label="Center"><AlignCenter className="h-3.5 w-3.5" /></button>
        <button type="button" className={`${btn} ${editor.isActive({ textAlign: "right" }) ? on : ""}`} onClick={() => editor.chain().focus().setTextAlign("right").run()} aria-label="Right"><AlignRight className="h-3.5 w-3.5" /></button>
        <div className="w-px h-5 bg-border mx-1" />
        <button type="button" className={btn} onClick={addLink} data-testid="rt-link" aria-label="Link"><Link2 className="h-3.5 w-3.5" /></button>
        <button type="button" className={btn} onClick={addImage} data-testid="rt-image" aria-label="Image"><ImageIcon className="h-3.5 w-3.5" /></button>
        <div className="w-px h-5 bg-border mx-1" />
        <button type="button" className={btn} onClick={() => editor.chain().focus().undo().run()} aria-label="Undo"><Undo2 className="h-3.5 w-3.5" /></button>
        <button type="button" className={btn} onClick={() => editor.chain().focus().redo().run()} aria-label="Redo"><Redo2 className="h-3.5 w-3.5" /></button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
